import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Stack, Typography, Tooltip, alpha, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  CalendarToday as TodayIcon,
} from '@mui/icons-material';
import {
  buildMonthGrid,
  isoOf,
  WEEKDAY_LABELS,
  DayCell,
} from '../../SiteSchedulesPage/calendarUtils';

export interface CalendarEvent {
  iso: string; // YYYY-MM-DD
  /** dot / chip 색상. 미지정 시 primary. */
  color?: 'primary' | 'secondary' | 'info' | 'warning' | 'error' | 'success' | 'default';
  /** lg 모드에서 셀 안에 표시할 짧은 텍스트 (예: 일정 제목). */
  label?: string;
  /** 호출측 식별자 — onEventClick 으로 그대로 돌려주므로 원본 entity 매핑에 사용. */
  id?: string;
}

interface MiniCalendarProps {
  events: CalendarEvent[];
  /** 'YYYY-MM-DD' 또는 null. 선택된 날짜는 강조 표시. */
  selectedIso: string | null;
  onSelect: (iso: string | null) => void;
  /**
   * 'sm' — 컴팩트(셀 정사각, dot 만). 사이드패널/모달용.
   * 'lg' — 큰 셀, 각 셀 안에 이벤트 label 1-2개 노출. 메인 영역용. 기본값.
   */
  size?: 'sm' | 'lg';
  /** "오늘" 버튼/타이틀 등 헤더 표시 여부. 기본 true */
  showHeader?: boolean;
  /**
   * 외부에서 일시적으로 강조할 날짜('YYYY-MM-DD'). 타임라인 hover 등에 사용.
   * selectedIso 와 별개로, 값이 바뀌고 다른 달이면 해당 달로 뷰를 이동한 뒤 강조한다.
   */
  highlightIso?: string | null;
  /**
   * lg 모드 셀 안의 이벤트 라벨 클릭 핸들러. 지정하면 라벨이 클릭 가능해지고
   * 셀(onSelect) 클릭과는 분리된다(stopPropagation).
   */
  onEventClick?: (event: CalendarEvent) => void;
  /**
   * 날짜를 이어 드래그해 기간을 선택했을 때 호출('YYYY-MM-DD' 시작/종료, 둘 다
   * 포함). 지정하면 셀을 누른 채 끌면 범위가 강조되고 놓는 순간 불린다.
   * 같은 셀에서 눌렀다 놓는 것은 드래그가 아니라 기존 클릭(onSelect)으로 동작.
   */
  onRangeSelect?: (startIso: string, endIso: string) => void;
}

const dotColor = (theme: Theme, color: CalendarEvent['color']): string => {
  switch (color) {
    case 'error':
      return theme.palette.error.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'info':
      return theme.palette.info.main;
    case 'success':
      return theme.palette.success.main;
    case 'secondary':
      return theme.palette.secondary.main;
    case 'default':
      return theme.palette.text.secondary;
    case 'primary':
    default:
      return theme.palette.primary.main;
  }
};

/**
 * 월 단위 캘린더. 외부 라이브러리 없이 calendarUtils 의 6주 그리드 사용.
 *
 * size='lg' (기본): 큰 셀, 각 셀 안에 이벤트 label 을 색상 칩으로 1-2개 노출.
 *   초과분은 "+n more" 로 표기. 한 눈에 일정 분포를 파악할 수 있는 메인 캘린더.
 * size='sm': 컴팩트 정사각 셀, 이벤트는 색상 dot 으로만 표기. 사이드/모달용.
 *
 * 셀 클릭 시 onSelect(iso) — 동일 셀 재클릭은 선택 해제(onSelect(null)).
 */
const MiniCalendar: React.FC<MiniCalendarProps> = ({
  events,
  selectedIso,
  onSelect,
  size = 'lg',
  showHeader = true,
  highlightIso = null,
  onEventClick,
  onRangeSelect,
}) => {
  const theme = useTheme();
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // 드래그 기간 선택 — 누른 셀(anchor)과 현재 지나는 셀. ISO(YYYY-MM-DD)는
  // 문자열 비교가 곧 날짜 비교라 정렬/범위 판정을 문자열로 한다.
  const [dragFrom, setDragFrom] = useState<string | null>(null);
  const [dragTo, setDragTo] = useState<string | null>(null);

  // mouseup 은 캘린더 밖에서 놓아도 드래그를 끝내야 하므로 window 에 건다.
  // 서로 다른 셀에서 눌렀다 놓으면 click 이벤트가 셀이 아닌 공통 조상에서
  // 발생하므로 셀 onClick(onSelect)과 겹치지 않는다.
  useEffect(() => {
    if (!dragFrom) return;
    const finish = () => {
      if (dragTo && dragTo !== dragFrom && onRangeSelect) {
        const [s, e] = dragFrom < dragTo ? [dragFrom, dragTo] : [dragTo, dragFrom];
        onRangeSelect(s, e);
      }
      setDragFrom(null);
      setDragTo(null);
    };
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [dragFrom, dragTo, onRangeSelect]);

  // 강조 날짜가 현재 보고 있는 달 밖이면 그 달로 이동(강조 셀이 화면에 보이도록).
  // null 로 풀릴 때(hover 해제)는 이동하지 않아 마지막 달을 유지한다.
  useEffect(() => {
    if (!highlightIso) return;
    const [y, m] = highlightIso.split('-').map(Number);
    if (!y || !m) return;
    setViewYear(y);
    setViewMonth(m - 1);
  }, [highlightIso]);

  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const eventByIso = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((e) => {
      const arr = map.get(e.iso);
      if (arr) arr.push(e);
      else map.set(e.iso, [e]);
    });
    return map;
  }, [events]);

  const handlePrev = () => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  };
  const handleNext = () => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  };
  const handleToday = () => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    onSelect(isoOf(now));
  };

  const handleCellClick = (cell: DayCell) => {
    if (selectedIso === cell.iso) {
      onSelect(null);
    } else {
      onSelect(cell.iso);
    }
  };

  // 드래그 중인 범위(시작·끝 포함). 셀 강조 판정에 사용.
  const dragMin = dragFrom && dragTo ? (dragFrom < dragTo ? dragFrom : dragTo) : null;
  const dragMax = dragFrom && dragTo ? (dragFrom < dragTo ? dragTo : dragFrom) : null;

  // 드래그 범위/외부 강조 셀 배경 — 다크모드는 어두운 바탕 위라 같은 알파면
  // 거의 안 보여서 더 진하게 깐다.
  const rangeBg = alpha(
    theme.palette.primary.main,
    theme.palette.mode === 'dark' ? 0.35 : 0.18,
  );

  // lg 모드일 때 셀당 최대 노출 라벨 수. 더 많으면 +n more 로 접음.
  const maxLabelsPerCell = size === 'lg' ? 3 : 0;
  const cellMinHeight = size === 'lg' ? 96 : undefined;
  const cellAspect = size === 'sm' ? '1 / 1' : 'auto';

  return (
    <Box>
      {showHeader && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mb: 1.5, justifyContent: 'space-between' }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton size="small" onClick={handlePrev} aria-label="이전 달">
              <PrevIcon fontSize="small" />
            </IconButton>
            <Typography
              variant={size === 'lg' ? 'h6' : 'subtitle2'}
              fontWeight={600}
              sx={{ minWidth: 110, textAlign: 'center' }}
            >
              {viewYear}년 {viewMonth + 1}월
            </Typography>
            <IconButton size="small" onClick={handleNext} aria-label="다음 달">
              <NextIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Tooltip title="오늘로 이동">
            <IconButton size="small" onClick={handleToday} aria-label="오늘">
              <TodayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: size === 'lg' ? 0 : 0.5,
          mb: size === 'lg' ? 0 : 0.5,
        }}
      >
        {WEEKDAY_LABELS.map((w, i) => (
          <Typography
            key={w}
            variant="caption"
            align="center"
            sx={{
              color: i === 0 ? 'error.light' : i === 6 ? 'info.light' : 'text.secondary',
              fontWeight: 600,
              py: size === 'lg' ? 1 : 0,
              borderBottom: size === 'lg' ? '1px solid' : 0,
              borderColor: 'divider',
              bgcolor: size === 'lg' ? alpha(theme.palette.action.hover, 0.3) : 'transparent',
            }}
          >
            {w}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: size === 'lg' ? 0 : 0.5,
          // 드래그 기간 선택 중 날짜/라벨 텍스트가 파랗게 긁히지 않게.
          ...(onRangeSelect && { userSelect: 'none' }),
          // lg 모드에서는 인접 셀이 보더를 공유하도록 외곽에만 보더
          ...(size === 'lg' && {
            borderLeft: '1px solid',
            borderTop: '1px solid',
            borderColor: 'divider',
          }),
        }}
      >
        {cells.map((cell) => {
          const dayEvents = eventByIso.get(cell.iso) ?? [];
          const isSelected = selectedIso === cell.iso;
          const isHighlight = highlightIso != null && highlightIso === cell.iso;
          // 드래그로 훑고 있는 범위 안의 셀 — 2칸 이상일 때만 (1칸은 그냥 클릭).
          const inDragRange =
            dragMin != null && dragMax != null && dragMin !== dragMax &&
            cell.iso >= dragMin && cell.iso <= dragMax;
          const ringed = isSelected || isHighlight;
          const weekday = cell.date.getDay();
          return (
            <Box
              key={cell.iso}
              onClick={() => handleCellClick(cell)}
              {...(onRangeSelect && {
                onMouseDown: (e: React.MouseEvent) => {
                  if (e.button !== 0) return;
                  // 드래그 중 브라우저 기본 텍스트 선택 시작을 막는다.
                  e.preventDefault();
                  setDragFrom(cell.iso);
                  setDragTo(cell.iso);
                },
                onMouseEnter: () => {
                  if (dragFrom) setDragTo(cell.iso);
                },
              })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCellClick(cell);
                }
              }}
              sx={{
                aspectRatio: cellAspect,
                minHeight: cellMinHeight,
                cursor: 'pointer',
                borderRadius: size === 'lg' ? 0 : 1,
                ...(size === 'lg'
                  ? {
                      borderRight: '1px solid',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      // 선택/강조 셀: inset shadow 로 강조 (보더 폭을 늘리면 grid 가 흔들림)
                      boxShadow: ringed
                        ? `inset 0 0 0 2px ${theme.palette.primary.main}`
                        : 'none',
                    }
                  : {
                      border: ringed ? '2px solid' : '1px solid',
                      borderColor: ringed ? 'primary.main' : 'divider',
                    }),
                bgcolor: inDragRange || isHighlight
                  ? rangeBg
                  : cell.isToday
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                opacity: cell.isCurrentMonth ? 1 : 0.45,
                display: 'flex',
                flexDirection: 'column',
                p: size === 'lg' ? 0.75 : 0.5,
                position: 'relative',
                transition: 'background-color 120ms',
                // hover 셀렉터가 기본 bgcolor 보다 우선하므로, 드래그 범위 안에서는
                // hover 색으로 덮이지 않게 범위 색을 유지한다 (마우스가 올라가 있는
                // 셀만 강조가 빠져 보이던 문제).
                '&:hover': {
                  bgcolor: inDragRange ? rangeBg : alpha(theme.palette.action.hover, 0.5),
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: size === 'lg' ? '0.8rem' : '0.7rem',
                  lineHeight: 1.2,
                  fontWeight: cell.isToday ? 700 : 500,
                  color:
                    !cell.isCurrentMonth
                      ? 'text.disabled'
                      : weekday === 0
                        ? 'error.main'
                        : weekday === 6
                          ? 'info.main'
                          : 'text.primary',
                  alignSelf: 'flex-start',
                  mb: size === 'lg' ? 0.5 : 0,
                }}
              >
                {cell.date.getDate()}
              </Typography>

              {/* lg: 셀 안에 이벤트 라벨 칩 (label 우선, 없으면 dot fallback) */}
              {size === 'lg' && dayEvents.length > 0 && (
                <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                  {dayEvents.slice(0, maxLabelsPerCell).map((e, idx) => {
                    const c = dotColor(theme, e.color);
                    const clickable = Boolean(onEventClick);
                    return e.label ? (
                      <Box
                        key={idx}
                        {...(clickable && {
                          role: 'button',
                          tabIndex: 0,
                          onClick: (ev: React.MouseEvent) => {
                            ev.stopPropagation();
                            onEventClick?.(e);
                          },
                          onKeyDown: (ev: React.KeyboardEvent) => {
                            if (ev.key === 'Enter' || ev.key === ' ') {
                              ev.preventDefault();
                              ev.stopPropagation();
                              onEventClick?.(e);
                            }
                          },
                        })}
                        sx={{
                          borderLeft: '3px solid',
                          borderColor: c,
                          bgcolor: alpha(c, 0.12),
                          color: 'text.primary',
                          pl: 0.5,
                          pr: 0.25,
                          py: 0.1,
                          borderRadius: 0.5,
                          fontSize: '0.65rem',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          ...(clickable && {
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(c, 0.28) },
                            '&:focus-visible': {
                              outline: '1.5px solid',
                              outlineColor: c,
                              outlineOffset: 1,
                            },
                          }),
                        }}
                        title={e.label}
                      >
                        {e.label}
                      </Box>
                    ) : (
                      <Box
                        key={idx}
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: c,
                        }}
                      />
                    );
                  })}
                  {dayEvents.length > maxLabelsPerCell && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.6rem',
                        lineHeight: 1,
                        color: 'text.secondary',
                        pl: 0.5,
                      }}
                    >
                      +{dayEvents.length - maxLabelsPerCell} more
                    </Typography>
                  )}
                </Stack>
              )}

              {/* sm: 하단 중앙 dot */}
              {size === 'sm' && dayEvents.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0.3}
                  sx={{ mt: 'auto', justifyContent: 'center', flexWrap: 'wrap', gap: 0.2 }}
                >
                  {dayEvents.slice(0, 3).map((e, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: dotColor(theme, e.color),
                      }}
                    />
                  ))}
                  {dayEvents.length > 3 && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.55rem', lineHeight: 1, color: 'text.secondary' }}
                    >
                      +{dayEvents.length - 3}
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MiniCalendar;
